// Full article bodies + downloadable template metadata for /resources
// Articles render on /resources/$slug. Templates are static files in /public/templates.

export type Article = {
  slug: string;
  title: string;
  tag: string;
  eta: string;
  summary: string;
  body: { heading?: string; paragraphs: string[]; list?: string[] }[];
};

export const ARTICLES: Article[] = [
  {
    slug: "ccma-30-day-referral-rule",
    title: "The 30-day CCMA referral rule, explained",
    tag: "Labour",
    eta: "5 min read",
    summary:
      "If you've been dismissed, you generally have 30 calendar days to refer the dispute to the CCMA. Here's what that means in practice and what to do if you've missed the deadline.",
    body: [
      {
        paragraphs: [
          "Section 191(1) of the Labour Relations Act gives a dismissed employee 30 calendar days from the date of dismissal to refer an unfair-dismissal dispute to the CCMA. For unfair labour practice disputes the period is 90 days from the act or omission complained of. Miss the date and the CCMA has no jurisdiction to hear your matter — unless you successfully apply for condonation.",
        ],
      },
      {
        heading: "How the 30 days are counted",
        paragraphs: [
          "The clock starts on the day after dismissal. Weekends and public holidays are included. The day you actually serve the LRA 7.11 referral form on the employer and file it with the CCMA is the day that counts — not the day you sign it. Email or hand-delivery is acceptable provided you can prove service.",
        ],
      },
      {
        heading: "What you need to refer",
        list: [
          "A completed LRA 7.11 referral form (one form per employee).",
          "Proof of dismissal (letter, payslip showing termination, or written statement of the events).",
          "Proof that you served the form on the employer (signed acknowledgement, registered post slip, or an email with read receipt).",
          "Your contact details and a brief description of the dispute and the relief you seek (reinstatement, re-employment or compensation).",
        ],
        paragraphs: [],
      },
      {
        heading: "If you missed the deadline",
        paragraphs: [
          "You may still refer the matter together with a condonation application. The CCMA weighs the degree of lateness, the reason for lateness, the prospects of success, the prejudice to the employer and the importance of the matter. A short delay with a strong reason and good prospects has a much better chance than a long delay with a weak explanation.",
          "Move quickly. The longer you wait, the harder condonation becomes. Draft a clear, signed affidavit setting out the facts, attach supporting documents, and file it with the referral.",
        ],
      },
      {
        heading: "Common mistakes",
        list: [
          "Counting working days instead of calendar days.",
          "Waiting for the outcome of an internal appeal — the 30 days run from the date of dismissal, not the appeal outcome.",
          "Referring to the wrong forum (e.g. bargaining council instead of the CCMA).",
          "Forgetting to serve the employer.",
        ],
        paragraphs: [],
      },
      {
        heading: "Need help?",
        paragraphs: [
          "We prepare LRA 7.11 referrals, condonation applications and the full case bundle. We do not represent you at the CCMA — for that, we refer to an admitted attorney. Book a consultation if you would like us to take a look at your matter.",
        ],
      },
    ],
  },
  {
    slug: "preparing-for-disciplinary-hearing",
    title: "Preparing for an internal disciplinary hearing",
    tag: "Labour",
    eta: "7 min read",
    summary:
      "A practical checklist for employees facing a disciplinary hearing — your rights, what to bring, and the procedural protections of the Code of Good Practice.",
    body: [
      {
        paragraphs: [
          "An internal disciplinary hearing is your employer's process for deciding whether you should be sanctioned for alleged misconduct. The Code of Good Practice: Dismissal (Schedule 8 to the LRA) sets the minimum fairness standards. Understanding your rights early changes the outcome.",
        ],
      },
      {
        heading: "Your procedural rights",
        list: [
          "Written notice of the charges with enough detail to prepare a defence.",
          "Reasonable time to prepare (typically at least 48 hours, longer for complex matters).",
          "Right to a representative — usually a fellow employee or shop steward.",
          "Right to call and cross-examine witnesses.",
          "Right to lead evidence in your defence.",
          "Right to an interpreter if needed.",
          "Right to a written outcome with reasons.",
          "Right to appeal (if the employer's policy allows it).",
        ],
        paragraphs: [],
      },
      {
        heading: "Before the hearing — your checklist",
        list: [
          "Read the charge sheet carefully. Note any vague wording, dates that don't match, or charges that overlap.",
          "Request the evidence pack: documents, CCTV, witness statements. You are entitled to know the case against you.",
          "List your witnesses. Speak to each one and confirm what they will say.",
          "Gather your documents: contract, policies, emails, performance records.",
          "Write a chronological timeline of events.",
          "Prepare your opening statement and a list of questions for the employer's witnesses.",
          "If you need a postponement, request it in writing with reasons.",
        ],
        paragraphs: [],
      },
      {
        heading: "At the hearing",
        paragraphs: [
          "Stay calm and respectful. Address the chairperson. Take notes. If you don't understand a question, ask for it to be repeated. If the employer raises new evidence, ask for time to consider it. Never sign anything at the hearing without reading it carefully.",
        ],
      },
      {
        heading: "After the hearing",
        paragraphs: [
          "You should receive a written outcome with reasons. If you are dismissed, you have 30 calendar days to refer the matter to the CCMA or a bargaining council. If your employer has an internal appeal, lodge it in writing within the time limit — but do not delay the CCMA referral; the 30 days run from the original dismissal date.",
        ],
      },
      {
        heading: "How we help",
        paragraphs: [
          "We prepare you for the hearing: we map the charges, build your evidence pack, brief witnesses, draft your opening and questions, and run a mock hearing so you walk in ready. Book a consultation to start.",
        ],
      },
    ],
  },
  {
    slug: "popia-for-small-business",
    title: "POPIA in plain English for small businesses",
    tag: "Compliance",
    eta: "8 min read",
    summary:
      "The eight conditions for lawful processing, what an Information Officer actually does, and a 7-step starting plan for SMEs.",
    body: [
      {
        paragraphs: [
          "The Protection of Personal Information Act (POPIA) applies to almost every South African business that handles personal information — which is almost every business. Compliance is not a one-off project; it is an operating discipline. The good news: a small business can reach a defensible baseline in a few weeks.",
        ],
      },
      {
        heading: "The eight conditions for lawful processing",
        list: [
          "Accountability — someone must own POPIA in your business (your Information Officer).",
          "Processing limitation — only collect what you need, only with a lawful basis (consent, contract, legal duty, legitimate interest).",
          "Purpose specification — tell people why you are collecting their information.",
          "Further processing limitation — don't repurpose data for something incompatible.",
          "Information quality — keep it accurate and up to date.",
          "Openness — publish a privacy notice that people can actually read.",
          "Security safeguards — appropriate technical and organisational measures.",
          "Data subject participation — let people access, correct or delete their data.",
        ],
        paragraphs: [],
      },
      {
        heading: "What does an Information Officer actually do?",
        paragraphs: [
          "By default the head of your business (the CEO, MD or sole proprietor) is the Information Officer. You can delegate specific duties to a deputy. The role is to register with the Information Regulator, develop and roll out compliance policies, handle data-subject requests, manage breaches, and report annually.",
        ],
      },
      {
        heading: "Your 7-step starting plan",
        list: [
          "1. Register your Information Officer with the Information Regulator (online, free).",
          "2. Map your data: what you collect, why, where it is stored, who it is shared with.",
          "3. Publish a plain-language privacy notice on your website and in your contracts.",
          "4. Get consent clauses into your client onboarding, employment and supplier contracts.",
          "5. Put basic security in place: strong passwords, MFA, backups, locked filing cabinets, access controls.",
          "6. Write a short breach-response plan so you know who does what if data is leaked.",
          "7. Train your team once a year and keep a register of training.",
        ],
        paragraphs: [],
      },
      {
        heading: "Common SME mistakes",
        list: [
          "Treating consent as a tickbox at the bottom of every form — consent must be specific, informed and voluntary.",
          "Forgetting employee data — payslips, ID copies and CVs are personal information too.",
          "Sharing client data with marketing tools without a written processor agreement.",
          "Keeping data 'forever' instead of having a retention schedule.",
        ],
        paragraphs: [],
      },
      {
        heading: "How we help",
        paragraphs: [
          "We run a POPIA readiness assessment, deliver a starter pack of policies (privacy notice, consent clauses, breach plan, retention schedule, processor agreement template), and train your team. Book a consultation to start.",
        ],
      },
    ],
  },
  {
    slug: "letter-of-demand-what-to-do",
    title: "Letter of demand: what it is and what to do",
    tag: "Debt",
    eta: "4 min read",
    summary:
      "Receiving a letter of demand isn't a court order. Learn how to respond, negotiate, or dispute — and when to escalate.",
    body: [
      {
        paragraphs: [
          "A letter of demand is a formal written request to pay an amount or perform an obligation, usually within a stated period. It is not a court order. It is, however, the standard first step before legal proceedings — so it must be taken seriously and responded to properly.",
        ],
      },
      {
        heading: "Step 1 — Read it carefully",
        list: [
          "Who is it from? An attorney, a debt collector, or the creditor directly?",
          "What is the exact amount and what does it relate to?",
          "What is the deadline to respond or pay?",
          "What action is threatened if you don't comply?",
          "Are interest, costs or 'collection charges' included? Are those amounts lawful?",
        ],
        paragraphs: [],
      },
      {
        heading: "Step 2 — Check the debt",
        paragraphs: [
          "Has the debt prescribed? Most contractual debts prescribe after three years of non-acknowledgement under the Prescription Act. Did you ever acknowledge the debt in writing, or make a payment? That resets the clock. Is the amount correct? Compare it to your statements and contract.",
        ],
      },
      {
        heading: "Step 3 — Respond in writing, on time",
        paragraphs: [
          "Never ignore a letter of demand. Even a short written response — acknowledging receipt and asking for information or proposing a payment arrangement — protects your position. If the debt is disputed, say so clearly and give reasons. If you cannot pay in full, propose a realistic arrangement in writing.",
        ],
      },
      {
        heading: "Step 4 — Negotiate",
        list: [
          "Ask for a settlement discount in exchange for early payment.",
          "Request a written payment arrangement with no further interest if you keep to the schedule.",
          "Ask for the original credit agreement and statements if you doubt the amount.",
          "Request that calls and collection contact happen only in writing.",
        ],
        paragraphs: [],
      },
      {
        heading: "When to escalate",
        paragraphs: [
          "If the letter is from an attorney, the amount is large, or you are at risk of summons or judgment, get help immediately. Judgment against you affects your credit profile for years and can lead to garnishee orders or attachment of assets.",
        ],
      },
      {
        heading: "How we help",
        paragraphs: [
          "We review the letter, check the debt and prescription, draft your response, and negotiate with the creditor or collector. We are not registered debt counsellors — for formal debt review we refer you to a registered counsellor. Book a consultation.",
        ],
      },
    ],
  },
  {
    slug: "court-online-basics",
    title: "Court Online basics for first-time users",
    tag: "Litigation",
    eta: "10 min read",
    summary:
      "A walkthrough of registration, profile setup, filing, bundles and service on Court Online and CaseLines.",
    body: [
      {
        paragraphs: [
          "Court Online is the South African judiciary's electronic case-management platform for civil matters in the High Court. CaseLines is the integrated digital evidence and bundle-management system. Together they have changed how matters are filed, managed and heard. This is a practical first-time user walkthrough.",
        ],
      },
      {
        heading: "1. Registration",
        paragraphs: [
          "Visit courtonline.judiciary.org.za and click Register. You will need a valid email address, a South African ID or passport number, and your professional details if you are an attorney, advocate or candidate attorney. Self-represented litigants register as 'individuals'. Verify your email and complete the profile.",
        ],
      },
      {
        heading: "2. Profile and roles",
        paragraphs: [
          "Set your correct role (attorney, candidate, advocate, individual). Attorneys must link to their firm. Add your physical address, service address and contact details — these become the default on every filing.",
        ],
      },
      {
        heading: "3. Issuing or uploading a case",
        list: [
          "Click 'Issue a new case' to create a new matter (e.g. summons, motion).",
          "Select the correct division and court.",
          "Capture parties accurately — name, capacity, attorneys.",
          "Upload your originating process (signed PDF).",
          "Pay any applicable fees. Court Online will issue and return a stamped copy.",
          "For existing matters, search by case number and 'request access' to be added as a party.",
        ],
        paragraphs: [],
      },
      {
        heading: "4. Filing further documents",
        paragraphs: [
          "Once you have access, use 'File a document' to upload notices, affidavits, pleadings and applications. Always file in PDF, properly paginated and OCR-searchable. Use clear file names that match the document type.",
        ],
      },
      {
        heading: "5. Service",
        paragraphs: [
          "Court Online does not replace formal service in every instance. Originating process generally still requires the sheriff. Subsequent filings between parties already on Court Online are served electronically through the platform. Always check the practice directives for your division.",
        ],
      },
      {
        heading: "6. CaseLines bundles",
        list: [
          "Each matter has a digital bundle in CaseLines.",
          "Upload documents into the correct section (pleadings, notices, affidavits, exhibits).",
          "Paginate so every page has a unique reference (e.g. 001-1, 002-1).",
          "Use the index feature so the judge can navigate.",
          "Update the bundle ahead of every hearing and notify the other side.",
        ],
        paragraphs: [],
      },
      {
        heading: "7. Common pitfalls",
        list: [
          "Filing scanned images instead of OCR-searchable PDFs.",
          "Wrong division or court selected at issue stage.",
          "Failing to serve hard copy where the rules still require it.",
          "Last-minute CaseLines uploads — judges expect bundles to be ready in advance.",
        ],
        paragraphs: [],
      },
      {
        heading: "Training and support",
        paragraphs: [
          "We offer Court Online and CaseLines onboarding for firms, paralegals and self-represented litigants — registration help, single-filing assistance, group training and written playbooks. Book a session to get your team running.",
        ],
      },
    ],
  },
  {
    slug: "workplace-policy-that-works",
    title: "Drafting a workplace policy that actually works",
    tag: "HR",
    eta: "6 min read",
    summary:
      "Why most policies fail, and a structure for writing policies that staff actually read, understand and follow.",
    body: [
      {
        paragraphs: [
          "Most workplace policies fail not because they are wrong, but because they are unread, untrained and unenforced. A policy that lives only in a folder on the share drive is not a defence — it is a liability. Here is how to write policies that actually change behaviour.",
        ],
      },
      {
        heading: "Why policies fail",
        list: [
          "Written in legalese employees can't decode.",
          "Cut-and-pasted from another organisation with no adaptation.",
          "Never properly rolled out, signed off or trained.",
          "Out of date — referencing repealed laws or old roles.",
          "Inconsistently enforced — applied to one employee and not another.",
        ],
        paragraphs: [],
      },
      {
        heading: "A structure that works",
        list: [
          "Purpose — one paragraph: why this policy exists and who it applies to.",
          "Definitions — only terms that are actually used in the policy.",
          "Principles — the rules, in plain language, in numbered clauses.",
          "Procedure — step-by-step what to do, who is responsible, what timelines apply.",
          "Consequences — what happens if the policy is breached.",
          "Review — version number, owner, review date.",
        ],
        paragraphs: [],
      },
      {
        heading: "Roll-out matters more than wording",
        paragraphs: [
          "Sign the policy. Train every employee. Capture a signed acknowledgement. Build a one-page summary for the noticeboard or intranet. Refresh training annually. Keep a register.",
        ],
      },
      {
        heading: "Common policies every SA employer should have",
        list: [
          "Code of conduct.",
          "Disciplinary code and grievance procedure.",
          "Leave policy.",
          "Health and safety policy.",
          "Information security and acceptable use.",
          "POPIA / privacy policy.",
          "Anti-harassment and harassment-reporting procedure.",
          "Social media and remote work policy.",
        ],
        paragraphs: [],
      },
      {
        heading: "How we help",
        paragraphs: [
          "We draft, review and roll out workplace policies tailored to your business. We also run the training and provide signed-acknowledgement registers. Book a consultation.",
        ],
      },
    ],
  },
];

export const getArticle = (slug: string) =>
  ARTICLES.find((a) => a.slug === slug);

// ---------- Templates (downloadable) ----------
export type TemplateMeta = {
  title: string;
  filename: string; // path under /public
  format: "RTF (opens in Word)" | "Text";
  summary: string;
};

export const TEMPLATES: TemplateMeta[] = [
  {
    title: "Letter of demand — basic template",
    filename: "/templates/letter-of-demand.rtf",
    format: "RTF (opens in Word)",
    summary:
      "Plain-language letter of demand for unpaid invoices or breach. Edit the bracketed fields.",
  },
  {
    title: "Employee grievance form",
    filename: "/templates/employee-grievance-form.rtf",
    format: "RTF (opens in Word)",
    summary:
      "Structured grievance form aligned to South African internal procedures.",
  },
  {
    title: "Disciplinary charge sheet template",
    filename: "/templates/disciplinary-charge-sheet.rtf",
    format: "RTF (opens in Word)",
    summary:
      "Charge sheet template compliant with the Code of Good Practice: Dismissal.",
  },
  {
    title: "POPIA consent clause (short form)",
    filename: "/templates/popia-consent-clause.rtf",
    format: "RTF (opens in Word)",
    summary:
      "Short-form consent clause for client onboarding, contracts and forms.",
  },
  {
    title: "Payment arrangement acknowledgement",
    filename: "/templates/payment-arrangement.rtf",
    format: "RTF (opens in Word)",
    summary:
      "Written acknowledgement of a payment plan between creditor and debtor.",
  },
  {
    title: "Simple service-level agreement skeleton",
    filename: "/templates/service-level-agreement.rtf",
    format: "RTF (opens in Word)",
    summary:
      "SLA skeleton — parties, scope, performance metrics, remedies and termination.",
  },
];
